import { type ReactNode } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import type {
  PatientStateWithContext,
  ResolvedSection136PlaceOfSafety,
} from "@thalamos/aspire-common";

import {
  PatientStateDisplay,
  WhatIsMhaStatusLink,
} from "./PatientStateDisplay.js";
import {
  Banner,
  BannerList,
  Button,
} from "~/components/design-system/index.js";

export type DualMhaStatusDisplayProps = {
  // The pre-existing, substantive detention (e.g. Section 2/3/17A) — the
  // "Existing MHA Status".
  existingState: PatientStateWithContext;
  // The Section 136 running alongside it — the "Coexisting MHA Status".
  coexistingState: PatientStateWithContext;
  showEditMhaStatus?: boolean;
  disableEditMHAStatusBtn?: boolean;
  // Routing/navigation stays with the caller — this component is presentational.
  onEditMhaStatus?: () => void;
  // Always applies to `coexistingState` — the Section 136 side, per the fixed
  // Existing/Coexisting mapping this component documents above.
  resolvedSection136PlaceOfSafety?: ResolvedSection136PlaceOfSafety;
};

function StatusCard(props: {
  heading: string;
  testId?: string;
  cardTestId?: string;
  children: ReactNode;
}) {
  const theme = useTheme();
  return (
    <Box
      data-testid={props.cardTestId}
      sx={{
        // Equal-width flexible columns: both grow from the same basis so they
        // are the same width side by side, and wrap to a full-width stack when
        // there isn't room for two.
        flex: "1 1 360px",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.common.lightGray}`,
        borderRadius: "6px",
        backgroundColor: "white",
        paddingTop: theme.spacing(1.5),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          columnGap: theme.spacing(1),
          // Align the heading with the card's internal left padding, and keep
          // the link clear of the right edge.
          paddingLeft: theme.spacing(2.5),
          paddingRight: theme.spacing(2.5),
        }}
      >
        <Typography
          data-testid={props.testId}
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            lineHeight: "18px",
            color: theme.palette.common.blueGray,
          }}
        >
          {props.heading}
        </Typography>
        <WhatIsMhaStatusLink />
      </Box>
      {props.children}
    </Box>
  );
}

/**
 * Renders a dual-MHA-status patient — a substantive non-136 detention running
 * alongside an active Section 136 — per the DEV-2269 design: a yellow warning
 * banner above two labelled, equal-size status cards, all wrapped in a box,
 * with the Edit action to the right of the cards.
 *
 * The "Existing/Coexisting" mapping (detention = Existing, Section 136 =
 * Coexisting) is decided by the caller in `PatientBanner` so this component
 * stays purely presentational; swap the two props there if the design changes.
 */
export function DualMhaStatusDisplay({
  existingState,
  coexistingState,
  showEditMhaStatus,
  disableEditMHAStatusBtn,
  onEditMhaStatus,
  resolvedSection136PlaceOfSafety,
}: DualMhaStatusDisplayProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box
      data-testid="patient-banner-dual-mha-status"
      sx={{
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.warning.main}`,
        borderRadius: "6px",
        overflow: "hidden",
      }}
    >
      <Banner
        bannerType={BannerList.WARNING}
        body={t("layout.patientBanner.dualMhaStatus.warning")}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          gap: theme.spacing(2),
          padding: theme.spacing(2),
          backgroundColor: "white",
        }}
      >
        <Box
          data-testid="patient-banner-both-statuses"
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "stretch",
            gap: theme.spacing(2),
          }}
        >
          <StatusCard
            testId="dual-mha-status-existing-heading"
            cardTestId="dual-mha-status-existing-card"
            heading={t("layout.patientBanner.dualMhaStatus.existingLabel")}
          >
            <PatientStateDisplay
              state={existingState}
              fullWidth
              hideMhaStatusInfoLink
            />
          </StatusCard>
          <StatusCard
            testId="dual-mha-status-coexisting-heading"
            cardTestId="dual-mha-status-coexisting-card"
            heading={t("layout.patientBanner.dualMhaStatus.coexistingLabel")}
          >
            <PatientStateDisplay
              state={coexistingState}
              fullWidth
              hideMhaStatusInfoLink
              resolvedSection136PlaceOfSafety={resolvedSection136PlaceOfSafety}
            />
          </StatusCard>
        </Box>
        {showEditMhaStatus && onEditMhaStatus && (
          <Box sx={{ alignSelf: "center", flexShrink: 0 }}>
            <Button
              label="Edit"
              onClick={onEditMhaStatus}
              disabled={disableEditMHAStatusBtn}
              testId="editMhaStatusButton"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
